/*
 * Copyright (c) AVPS Technologies Inc 2025
 *
 * Loads the signed-in student's subscription-course details for the purchase
 * section of the profile.
 *
 * The pieces live in three places, so they are stitched together here:
 *   - user.subscription        the MemberSubscription (status, next billing
 *                              date, subscribed product references, bills)
 *   - product/{id}             the course itself, for its name and recurring price
 *   - invoice find/criteria    the amount and payment status of each bill, which
 *                              the embedded bill reference only points at by id
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { findInvoicesByCriteria } from '../../../../../../api/action/invoice';
import { getProductById } from '../../../../../../api/action/products';
import { fetchUser } from '../../../../../../api/action/user';
import {
    getBillPaymentStatus,
    getSubscriptionBillingCycle,
    getSubscriptionPlanAmount,
    getSubscriptionPlanName,
    isSubscriptionEnabledCompany,
    isSubscriptionProduct,
} from '../../../../../../utils/subscriptionDisplay';
import { getOrderLineItems } from '../utils/orderUtils';

const extractUser = (response) => {
    if (!response) return null;
    if (Array.isArray(response)) return response[0] || null;
    if (Array.isArray(response?.data)) return response.data[0] || null;
    if (Array.isArray(response?.payload)) return response.payload[0] || null;
    if (response?.['0'] && typeof response['0'] === 'object') return response['0'];
    if (response?.payload?.primaryId) return response.payload;
    if (response?.data?.primaryId) return response.data;
    if (response?.primaryId) return response;
    return null;
};

const extractProduct = (response) => {
    if (!response) return null;
    if (response?.data?.primaryId) return response.data;
    if (response?.payload?.data?.primaryId) return response.payload.data;
    if (response?.primaryId) return response;
    return null;
};

const extractInvoiceList = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.invoices)) return response.invoices;
    if (Array.isArray(response?.payload)) return response.payload;
    if (response?.primaryId) return [response];
    return [];
};

/** Milliseconds-comparable timestamp for a date-ish value, or null. */
const toTime = (value) => {
    if (!value) return null;
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
};

/** True when one of the order's line items is for the given course. */
const orderCoversProduct = (order, productId) => getOrderLineItems(order).some((lineItem) => {
    const linkedId = lineItem?.product?.primaryId
        || lineItem?.scheduledProduct?.product?.primaryId
        || lineItem?.productId;
    return linkedId && String(linkedId) === String(productId);
});

/**
 * The date the subscription started.
 *
 * A MemberSubscription carries no creation timestamp of its own, so the start
 * date is the earliest order that paid for the course. An effective date is
 * preferred when one happens to be set, which is the case for subscriptions the
 * admin console has edited.
 */
const resolveCreatedDate = (subscription, productId, orders = []) => {
    const effective = subscription?.effectiveDate;
    const fromSubscription = effective?.effectiveStartDate || effective?.localStartDate;
    if (fromSubscription) return fromSubscription;

    if (!productId) return null;

    const orderDates = orders
        .filter((order) => orderCoversProduct(order, productId))
        .map((order) => toTime(order?.createdDate))
        .filter((time) => time !== null);

    if (orderDates.length === 0) return null;
    return new Date(Math.min(...orderDates)).toISOString();
};

/**
 * Builds the bill rows shown under a subscribed course, newest first, joining
 * each embedded bill reference to the invoice that carries its amount and
 * payment status.
 */
const buildBillRows = (subscription, invoicesById) => {
    const bills = Array.isArray(subscription?.subscriptionBills) ? subscription.subscriptionBills : [];

    return bills
        .filter(Boolean)
        .map((bill, index) => {
            const invoiceRef = bill?.invoice || {};
            const invoice = invoicesById.get(String(invoiceRef.primaryId)) || invoiceRef;
            const order = invoice?.order || null;
            const period = bill?.billingPeriodEffectiveDate || {};

            return {
                key: bill?.primaryId || invoiceRef?.primaryId || `bill-${index}`,
                invoiceNumber: invoice?.number || invoiceRef?.number || '',
                invoiceDate: invoice?.date || null,
                dueDate: invoice?.dueDate || bill?.autoDebitDate || null,
                periodStart: period?.effectiveStartDate || period?.localStartDate || null,
                periodEnd: period?.effectiveEndDate || period?.localEndDate || null,
                amount: order?.totalAmount ?? order?.orderTotal ?? null,
                trialBilling: Boolean(bill?.trialBilling),
                paymentStatus: getBillPaymentStatus(invoice, order),
            };
        })
        .sort((a, b) => (toTime(b.invoiceDate) || toTime(b.periodStart) || 0)
            - (toTime(a.invoiceDate) || toTime(a.periodStart) || 0));
};

/**
 * @param {Array} orders orders already loaded for the page, used only to date
 *                       subscriptions that predate the effective-date field
 */
const useMemberSubscription = (orders = []) => {
    const dispatch = useDispatch();
    const companyDetails = useSelector((state) => state?.companyReducer?.company);
    const userDetail = useSelector((state) => state?.auth?.data) || {};

    const subscriptionEnabled = isSubscriptionEnabledCompany(companyDetails);
    const memberPrimaryId = userDetail?.primaryId || '';
    const companyPrimaryId = companyDetails?.primaryId || '';

    const [subscription, setSubscription] = useState(null);
    const [products, setProducts] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const loadSubscription = useCallback(async () => {
        const userResponse = await dispatch(fetchUser({
            primaryId: memberPrimaryId,
            ...(companyPrimaryId ? { company: { primaryId: companyPrimaryId } } : {}),
        }));

        const member = extractUser(userResponse);
        const memberSubscription = member?.subscription || null;

        if (!memberSubscription) {
            return { memberSubscription: null, subscribedProducts: [], memberInvoices: [] };
        }

        const productRefs = Array.isArray(memberSubscription.subscribedProducts)
            ? memberSubscription.subscribedProducts.filter((product) => product?.primaryId)
            : [];

        // The subscription only stores product references, so each course is
        // loaded for its name and recurring price. One failing course must not
        // hide the rest, hence the per-product catch.
        const subscribedProducts = (await Promise.all(
            productRefs.map(async (productRef) => {
                try {
                    const product = extractProduct(await dispatch(getProductById(productRef.primaryId)));
                    return product || { ...productRef, name: '' };
                } catch {
                    return { ...productRef, name: '' };
                }
            })
        )).filter(Boolean);

        let memberInvoices = [];
        if (Array.isArray(memberSubscription.subscriptionBills) && memberSubscription.subscriptionBills.length > 0) {
            try {
                memberInvoices = extractInvoiceList(await dispatch(findInvoicesByCriteria({
                    user: { primaryId: memberPrimaryId },
                    ...(companyPrimaryId ? { company: { primaryId: companyPrimaryId } } : {}),
                })));
            } catch {
                // Bills still render without them, just without amounts.
                memberInvoices = [];
            }
        }

        return { memberSubscription, subscribedProducts, memberInvoices };
    }, [companyPrimaryId, dispatch, memberPrimaryId]);

    useEffect(() => {
        if (!subscriptionEnabled || !memberPrimaryId) {
            setSubscription(null);
            setProducts([]);
            setInvoices([]);
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        loadSubscription()
            .then(({ memberSubscription, subscribedProducts, memberInvoices }) => {
                if (cancelled) return;
                setSubscription(memberSubscription);
                setProducts(subscribedProducts);
                setInvoices(memberInvoices);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err?.message || 'Failed to load subscription details');
                setSubscription(null);
                setProducts([]);
                setInvoices([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [loadSubscription, memberPrimaryId, subscriptionEnabled]);

    const invoicesById = useMemo(() => new Map(
        invoices.filter((invoice) => invoice?.primaryId).map((invoice) => [String(invoice.primaryId), invoice])
    ), [invoices]);

    // Bills belong to the subscription as a whole rather than to one course, so
    // every subscribed course shows the same billing history.
    const billRows = useMemo(
        () => buildBillRows(subscription, invoicesById),
        [invoicesById, subscription]
    );

    const subscriptionCourses = useMemo(() => {
        if (!subscription) return [];

        return products.map((product, index) => ({
            key: product?.primaryId || `subscription-course-${index}`,
            courseName: product?.name || 'Subscription Course',
            planName: getSubscriptionPlanName(product),
            planAmount: getSubscriptionPlanAmount(product),
            billingCycle: getSubscriptionBillingCycle(product),
            createdDate: resolveCreatedDate(subscription, product?.primaryId, orders),
            status: subscription?.status || '',
            nextBillingDate: subscription?.nextBillingDate || null,
            autoPaymentEnabled: Boolean(subscription?.autoPaymentEnabled),
            isTrial: Boolean(subscription?.isTrial ?? subscription?.trial),
            // A course loaded without its type still belongs to the
            // subscription, so treat a missing subtype as a subscription course.
            isSubscriptionCourse: product?.type ? isSubscriptionProduct(product) : true,
            bills: billRows,
        }));
    }, [billRows, orders, products, subscription]);

    return {
        subscriptionEnabled,
        subscription,
        subscriptionCourses,
        loading,
        error,
    };
};

export default useMemberSubscription;
